import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { KTSVG, SimpleAlert } from '../../../../../rbts/helpers';
import { NivelEntradaModel } from '../models/NivelEntradaModel';
import { crearNivel, actualizarNivel } from '../../../../service/boletera/EventosBoletera';

type Props = {
  show: boolean;
  handleClose: (refresh: boolean) => void;
  eventoId: number;
  nivel?: NivelEntradaModel | null;
  aforoDisponible: number;
};

const schema = Yup.object().shape({
  nombre: Yup.string().trim().required('El nombre del nivel es requerido.'),
  precio: Yup.number().typeError('Debe ser un número.').min(0, 'Debe ser mayor o igual a cero.').required('El precio es requerido.'),
  modo_venta: Yup.string().oneOf(['numerado', 'general_admission']).required(),
  cantidad_boletos: Yup.number()
    .typeError('Debe ser un número.')
    .positive('Debe ser mayor a cero.')
    .integer('Debe ser entero.')
    .required('La cantidad de boletos es requerida.'),
});

const ModalNivel: React.FC<Props> = ({ show, handleClose, eventoId, nivel, aforoDisponible }) => {
  const [loading, setLoading] = useState(false);
  const isEditing = !!nivel;

  const form = useFormik({
    initialValues: {
      nombre: nivel?.nombre || '',
      precio: nivel?.precio ?? '',
      modo_venta: nivel?.modo_venta || 'general_admission',
      cantidad_boletos: nivel?.cantidad_boletos ?? '',
    },
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          nombre: values.nombre,
          precio: Number(values.precio),
          modo_venta: values.modo_venta as 'numerado' | 'general_admission',
          cantidad_boletos: Number(values.cantidad_boletos),
        };
        if (isEditing && nivel) {
          await actualizarNivel(eventoId, nivel.id, payload);
          SimpleAlert('Nivel actualizado correctamente', 2500, 'success');
        } else {
          await crearNivel(eventoId, payload);
          SimpleAlert('Nivel creado correctamente', 2500, 'success');
        }
        form.resetForm();
        handleClose(true);
      } catch (error: any) {
        SimpleAlert(error?.response?.data?.message || 'Error al guardar el nivel.', 3000, 'error');
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!show) { form.resetForm(); }
  }, [show]);

  const cantidadActual = Number(form.values.cantidad_boletos) || 0;
  const disponibleConEste = isEditing
    ? aforoDisponible + (nivel?.cantidad_boletos || 0)
    : aforoDisponible;
  const excede = cantidadActual > disponibleConEste;

  return (
    <Modal
      id="kt_modal_nivel_entrada"
      tabIndex={-1}
      aria-hidden="true"
      backdrop="static"
      dialogClassName="modal-dialog mw-500px"
      show={show}
      onHide={() => { form.resetForm(); handleClose(false); }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="text-muted">{isEditing ? 'Editar nivel' : 'Agregar nivel'}</h2>
          <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => { form.resetForm(); handleClose(false); }}>
            <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
          </div>
        </div>
        <div className="modal-body mx-5">
          <form onSubmit={form.handleSubmit}>
            {/* Nombre */}
            <div className="mb-5">
              <label className="required fw-bold fs-6 mb-2">Nombre</label>
              <input className="form-control" placeholder="Ej: General, VIP, Especial" {...form.getFieldProps('nombre')} />
              {form.touched.nombre && form.errors.nombre && (
                <div className="fv-plugins-message-container text-danger"><span>{form.errors.nombre}</span></div>
              )}
            </div>
            {/* Precio */}
            <div className="mb-5">
              <label className="required fw-bold fs-6 mb-2">Precio ($)</label>
              <input className="form-control" type="number" min={0} step="0.01" placeholder="Ej: 450" {...form.getFieldProps('precio')} />
              {form.touched.precio && form.errors.precio && (
                <div className="fv-plugins-message-container text-danger"><span>{form.errors.precio as string}</span></div>
              )}
            </div>
            {/* Modo de venta */}
            <div className="mb-5">
              <label className="required fw-bold fs-6 mb-2">Modo de venta</label>
              <select className="form-select" {...form.getFieldProps('modo_venta')}>
                <option value="general_admission">Admisión general</option>
                <option value="numerado">Numerado</option>
              </select>
            </div>
            {/* Cantidad de boletos */}
            <div className="mb-5">
              <label className="required fw-bold fs-6 mb-2">Cantidad de boletos</label>
              <input
                className={`form-control ${excede ? 'is-invalid' : ''}`}
                type="number"
                min={1}
                placeholder="Ej: 3000"
                {...form.getFieldProps('cantidad_boletos')}
              />
              {excede && (
                <div className="fv-plugins-message-container text-danger mt-1">
                  <span>Excede el aforo disponible ({disponibleConEste.toLocaleString()} boletos).</span>
                </div>
              )}
              {!excede && disponibleConEste > 0 && (
                <div className="text-muted fs-7 mt-1">
                  Disponible para asignar: {disponibleConEste.toLocaleString()}
                </div>
              )}
              {form.touched.cantidad_boletos && form.errors.cantidad_boletos && (
                <div className="fv-plugins-message-container text-danger"><span>{form.errors.cantidad_boletos as string}</span></div>
              )}
            </div>

            <div className="d-flex justify-content-end mt-6">
              <button type="reset" className="btn btn-light me-3" onClick={() => { form.resetForm(); handleClose(false); }}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={loading || excede}>
                {loading ? (
                  <span className="indicator-progress" style={{ display: 'block' }}>
                    Espere...<span className="spinner-border spinner-border-sm align-middle ms-2" />
                  </span>
                ) : (
                  <span className="indicator-label">Guardar</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export { ModalNivel };
